import type { Coord, FacingDir, Unit } from "@dailywars/core/engine";
import { facingFromPath, isAirUnit } from "@dailywars/core/engine";

import {
  ATTACK_FX_BULLET_DURATION_MS,
  ATTACK_FX_CORE_DURATION_MS,
  UNIT_DEATH_EXPLOSION_END,
  UNIT_DEATH_EXPLOSION_START,
} from "#/game/animation/constants";
import type {
  CanvasAttackFxAnimation,
  CanvasBoardAnimation,
  CanvasMoveAnimation,
} from "#/game/animation/types";
import { explosionFrameCount, type ExplosionKind } from "#/game/fxSprites";

function attackFxBulletMs(animation: CanvasAttackFxAnimation): number {
  return animation.direct ? 0 : ATTACK_FX_BULLET_DURATION_MS;
}

export function attackFxCoreProgress(animation: CanvasAttackFxAnimation, nowMs: number): number {
  const elapsed = Math.max(0, nowMs - animation.startedAtMs);
  return Math.min(1, (elapsed - attackFxBulletMs(animation)) / ATTACK_FX_CORE_DURATION_MS);
}

export function attackFxBulletProgress(animation: CanvasAttackFxAnimation, nowMs: number): number {
  const bulletMs = attackFxBulletMs(animation);
  if (bulletMs <= 0) return 1;
  return Math.max(0, nowMs - animation.startedAtMs) / bulletMs;
}

export interface UnitDeathDrawEffect {
  showUnit: boolean;
  explosionKind: ExplosionKind | null;
  explosionFrame: number;
}

function moveProgress(animation: CanvasMoveAnimation, nowMs: number): number {
  const elapsed = Math.max(0, nowMs - animation.startedAtMs);
  return Math.min(1, elapsed / Math.max(1, animation.durationMs));
}

export function getAnimatedPosition(
  animation: CanvasMoveAnimation,
  nowMs: number,
): { x: number; y: number } {
  const nodes = [animation.from, ...animation.path];
  if (nodes.length < 2) {
    return animation.from;
  }

  const totalSegments = nodes.length - 1;
  const progress = moveProgress(animation, nowMs);
  const scaled = progress * totalSegments;
  const segmentIndex = Math.min(totalSegments - 1, Math.floor(scaled));
  const t = scaled - segmentIndex;
  const from = nodes[segmentIndex]!;
  const to = nodes[segmentIndex + 1]!;
  return {
    x: from.x + (to.x - from.x) * t,
    y: from.y + (to.y - from.y) * t,
  };
}

export function isAnimationComplete(animation: CanvasBoardAnimation, nowMs: number): boolean {
  return nowMs - animation.startedAtMs >= animation.durationMs;
}

export function getUnitDeathDrawEffect({
  unit,
  animation,
  nowMs,
  isGhost,
}: {
  unit: Unit;
  animation: CanvasBoardAnimation | null;
  nowMs: number;
  isGhost: boolean;
}): UnitDeathDrawEffect | null {
  if (!isGhost || !animation) return null;
  const ghostIds = new Set(
    (animation.type === "move" || animation.type === "attackFx"
      ? (animation.ghostUnits ?? [])
      : []
    ).map((ghost) => ghost.id),
  );
  if (!ghostIds.has(unit.id)) return null;
  if (animation.type !== "attackFx") return null;

  const t = attackFxCoreProgress(animation, nowMs);
  const explosionKind: ExplosionKind = isAirUnit(unit.kind) ? "air" : "land";
  return deathExplosionFromCoreProgress({ t, explosionKind });
}

export function getCannonDeathDrawEffect({
  pos,
  animation,
  nowMs,
}: {
  pos: Coord;
  animation: CanvasBoardAnimation | null;
  nowMs: number;
}): UnitDeathDrawEffect | null {
  if (!animation) return null;
  const ghosts =
    animation.type === "move" || animation.type === "attackFx"
      ? (animation.ghostCannons ?? [])
      : [];
  if (!ghosts.some((ghost) => ghost.pos.x === pos.x && ghost.pos.y === pos.y)) return null;
  if (animation.type !== "attackFx") {
    return { showUnit: true, explosionKind: null, explosionFrame: 0 };
  }
  return deathExplosionFromCoreProgress({
    t: attackFxCoreProgress(animation, nowMs),
    explosionKind: "land",
  });
}

function deathExplosionFromCoreProgress({
  t,
  explosionKind,
}: {
  t: number;
  explosionKind: ExplosionKind;
}): UnitDeathDrawEffect {
  if (t < UNIT_DEATH_EXPLOSION_START) {
    return { showUnit: true, explosionKind: null, explosionFrame: 0 };
  }
  if (t >= UNIT_DEATH_EXPLOSION_END) {
    return { showUnit: false, explosionKind: null, explosionFrame: 0 };
  }

  const localT =
    (t - UNIT_DEATH_EXPLOSION_START) / (UNIT_DEATH_EXPLOSION_END - UNIT_DEATH_EXPLOSION_START);
  const frames = explosionFrameCount(explosionKind);
  const explosionFrame = Math.min(frames - 1, Math.floor(localT * frames));
  return { showUnit: false, explosionKind, explosionFrame };
}

const HIT_FLASH_WINDOW = 0.4;
const HIT_FLASH_HOLD = 0.15;

function combatHitFlash(coreT: number): number {
  const localT = coreT / HIT_FLASH_WINDOW;
  if (localT < 0 || localT >= 1) return 0;
  if (localT < HIT_FLASH_HOLD) return 1;
  return (1 - (localT - HIT_FLASH_HOLD) / (1 - HIT_FLASH_HOLD)) ** 2;
}

export function getUnitAttackFlash({
  pos,
  animation,
  nowMs,
}: {
  pos: Coord;
  animation: CanvasBoardAnimation | null;
  nowMs: number;
}): number {
  if (animation?.type !== "attackFx") return 0;
  const isTarget = pos.x === animation.targetTile.x && pos.y === animation.targetTile.y;
  const isCounterTarget =
    animation.hasCounter &&
    pos.x === animation.attackerTile.x &&
    pos.y === animation.attackerTile.y;
  if (!isTarget && !isCounterTarget) return 0;
  return combatHitFlash(attackFxCoreProgress(animation, nowMs));
}

export function getAnimatedFacing({
  animation,
  nowMs,
  fallback,
}: {
  animation: CanvasMoveAnimation;
  nowMs: number;
  fallback: FacingDir;
}): FacingDir {
  const nodes = [animation.from, ...animation.path];
  if (nodes.length < 2) return fallback;
  const totalSegments = nodes.length - 1;
  const progress = moveProgress(animation, nowMs);
  const segmentIndex = Math.min(totalSegments - 1, Math.floor(progress * totalSegments));
  const partialPath = nodes.slice(1, segmentIndex + 2);
  return facingFromPath({
    path: partialPath,
    startPos: animation.from,
    fallback,
  });
}
